import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card } from '@/components/ui/cards';
import { Selector, type SelectorOption } from '@/components/ui/inputs/selector';
import { PlaceholderContent } from '@/components/ui/layout';
import { ActionMenu, type ActionMenuItem } from '@/components/ui/menus';
import { Table } from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import { useMyTeamFilters, toNumbers } from '@/hooks/attendance/myTeam/useMyTeamFilters';
import { useMyTeamMonthlySummaryFilters } from '@/hooks/attendance/myTeam/useMyTeamMonthlySummaryFilters';
import { useMyTeamAttendanceSummary } from '@/hooks/attendance/myTeam/useMyTeamAttendanceSummary';
import { useMyTeamTardinessSummary } from '@/hooks/attendance/myTeam/useMyTeamTardinessSummary';
import { useMyTeamTardinessDetailNavigation } from '@/hooks/attendance/myTeam/useMyTeamTardinessDetailNavigation';
import { useMyTeamAbsenceSummary } from '@/hooks/attendance/myTeam/useMyTeamAbsenceSummary';
import { useMyTeamMarksSummary } from '@/hooks/attendance/myTeam/useMyTeamMarksSummary';
import { useMyTeamMonthlyOvertimeSummary } from '@/hooks/attendance/myTeam/useMyTeamMonthlyOvertimeSummary';
import { useMyTeamMonthlyTardinessSummary } from '@/hooks/attendance/myTeam/useMyTeamMonthlyTardinessSummary';
import { useMyTeamMonthlyAbsenceSummary } from '@/hooks/attendance/myTeam/useMyTeamMonthlyAbsenceSummary';
import { useMyTeamMonthlyMarksSummary } from '@/hooks/attendance/myTeam/useMyTeamMonthlyMarksSummary';
import { useMyTeamMonthlyEvolution } from '@/hooks/attendance/myTeam/useMyTeamMonthlyEvolution';
import { useMyTeamTodayEmployeeStatusList } from '@/hooks/attendance/myTeam/useMyTeamTodayEmployeeStatusList';
import { ROUTE_DEFINITIONS } from '@/routes';
import { formatDateToYYYYMMDD } from '@/utils';
import type { BarChartDataPoint } from '@/components/ui/charts/BarChart';

import { DonutSummaryCard } from './DonutSummaryCard';
import { DonutSummaryCardSkeleton } from './DonutSummaryCardSkeleton';
import { MyTeamMonthlySummaryFilters } from './MyTeamMonthlySummaryFilters';
import { BarChartCard } from './BarChartCard';
import {
  getAttendanceSituationColor,
  getTardinessSituationColor,
  getAbsenceSituationColor,
  getMonthlyOvertimeColor,
  getMonthlyTardinessColor,
  getMonthlyAbsenceColor,
  MY_TEAM_EMPTY_STATE_COLOR,
} from './myTeamThresholds';

// Código de la sección "Situación de asistencia" (ASIS-5404 / REXASIS-003). Debe
// coincidir con LIBC.Attendance.MyTeam.Common.MyTeamDashboardSectionConstants
// .SITUACION_ASISTENCIA del backend.
const SITUACION_ASISTENCIA_SECTION_CODE = 'SITUACION_ASISTENCIA';

// Código de la sección "Resumen del mes" (ASIS-5414). Debe coincidir con
// LIBC.Attendance.MyTeam.Common.MyTeamDashboardSectionConstants.RESUMEN_MES del
// backend. Horas extras (ASIS-5409), Atrasos (ASIS-5410), Ausencias (ASIS-5411) y
// Marcajes (ASIS-5412) ya están implementadas — las 4 tarjetas de la sección están
// completas; ASIS-5413 agrega 3 gráficos de barra (evolución mensual) DENTRO de este
// mismo panel "Resumen del mes", en una segunda fila debajo de las tarjetas — no es
// una sección nueva del filtro "Secciones".
const RESUMEN_MES_SECTION_CODE = 'RESUMEN_MES';

export interface MyTeamTabContentProps {
  // Reporta el estado de carga al dashboard padre: el botón global "Actualizar"
  // necesita saber si la pestaña activa ya terminó de cargar, aunque esta pestaña
  // administre su propio hook de datos.
  onLoadingChange?: (isLoading: boolean) => void;
}

// "Mi equipo" tab content (ASIS-5402 / REXASIS-001 — first of a 13-ticket epic).
// Owns the 3-filter panel (Secciones / Empresas / Colaboradores) and renders one
// container per section returned by the backend. "Situación de asistencia" (ASIS-5404
// a 5408) and the "Resumen del mes" panel — Horas extras/Atrasos/Ausencias/Marcajes
// cards (ASIS-5409/5410/5411/5412) plus the 3 monthly-evolution bar charts
// (ASIS-5413) — ship real content, all inside the same "Resumen del mes" Card.
export const MyTeamTabContent: React.FC<MyTeamTabContentProps> = ({
  onLoadingChange,
}) => {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const {
    isLoading,
    filters,
    sections,
    companies,
    catalogOptions,
    visibleSectionCodes,
    isEmployeesLoading,
    handlers,
    buildSectionHeaderDate,
  } = useMyTeamFilters(onLoadingChange);

  // Empresa única del filtro general (arriba) cuando ese multi-select tiene
  // EXACTAMENTE una empresa marcada; null con 0 ("Todas") o 2+ seleccionadas. Ambos
  // selectores de Empresa (filtro general y "Resumen del mes") deben reflejar el
  // mismo valor cuando corresponde — ver useMyTeamMonthlySummaryFilters.
  const globalSelectedCompanyId = filters.companyIds.length === 1 ? filters.companyIds[0] : null;

  // Cuando el usuario cambia la Empresa DESDE "Resumen del mes", el filtro general
  // pasa a tener únicamente esa empresa seleccionada (sincronía en la otra dirección).
  const onMonthlySummaryCompanyChange = useCallback(
    (companyId: string) => handlers.onCompanyChange([companyId]),
    [handlers],
  );

  // "Resumen del mes" filters panel (ASIS-5414). Reuses the companies catalog already
  // loaded by useMyTeamFilters above instead of re-fetching it — same list as the
  // general Empresa filter, only really-supervised companies. Called unconditionally
  // per rules of hooks — the section itself may not be visible depending on the
  // Secciones filter, but the hook call must not be conditional.
  const monthlySummaryFilters = useMyTeamMonthlySummaryFilters(
    companies,
    globalSelectedCompanyId,
    onMonthlySummaryCompanyChange,
  );

  // "Resumen del mes" — Horas extras (ASIS-5409) y Atrasos (ASIS-5410). Ambas tarjetas
  // comparten estos ids. selectedCompanyId/selectedMonthlyCutId llegan como string desde
  // el panel de filtros; se convierten a number|null (los hooks omiten el fetch cuando
  // monthlyCutId es null, ej. empresa sin cortes disponibles).
  const monthlySummaryCompanyId = Number(monthlySummaryFilters.selectedCompanyId) || null;
  const monthlySummaryMonthlyCutId = Number(monthlySummaryFilters.selectedMonthlyCutId) || null;
  const {
    summary: overtimeSummary,
    isLoading: isOvertimeLoading,
  } = useMyTeamMonthlyOvertimeSummary(monthlySummaryCompanyId, monthlySummaryMonthlyCutId);
  const {
    summary: tardinessMonthlySummary,
    isLoading: isTardinessMonthlyLoading,
  } = useMyTeamMonthlyTardinessSummary(monthlySummaryCompanyId, monthlySummaryMonthlyCutId);
  const {
    summary: monthlyAbsenceSummary,
    isLoading: isAbsenceMonthlyLoading,
  } = useMyTeamMonthlyAbsenceSummary(monthlySummaryCompanyId, monthlySummaryMonthlyCutId);
  const {
    summary: monthlyMarksSummary,
    isLoading: isMarksMonthlyLoading,
  } = useMyTeamMonthlyMarksSummary(monthlySummaryCompanyId, monthlySummaryMonthlyCutId);
  // "Resumen del mes" — evolución mensual (ASIS-5413 / REXASIS-012). Same 2 ids as the
  // 4 cards above; the 3 bar charts render inside the same "Resumen del mes" panel, not
  // a new section.
  const {
    evolution: monthlyEvolution,
    isLoading: isEvolutionLoading,
  } = useMyTeamMonthlyEvolution(monthlySummaryCompanyId, monthlySummaryMonthlyCutId);

  // Recalcula "Situación de asistencia" solo cuando cambian los filtros de
  // Empresa/Colaborador del dashboard (no cuando cambian Secciones), por regla de
  // negocio. Memoizado para que la conversión a number[] no dispare un refetch en
  // cada render — solo cuando `filters.companyIds`/`filters.employeeIds` cambian.
  const companyIdNumbers = useMemo(() => toNumbers(filters.companyIds), [filters.companyIds]);
  const employeeIdNumbers = useMemo(() => toNumbers(filters.employeeIds), [filters.employeeIds]);
  const { summary, isLoading: isSummaryLoading } = useMyTeamAttendanceSummary(
    companyIdNumbers,
    employeeIdNumbers,
  );
  const { summary: tardinessSummary, isLoading: isTardinessLoading } = useMyTeamTardinessSummary(
    companyIdNumbers,
    employeeIdNumbers,
  );
  const { summary: absenceSummary, isLoading: isAbsenceLoading } = useMyTeamAbsenceSummary(
    companyIdNumbers,
    employeeIdNumbers,
  );
  const { summary: marksSummary, isLoading: isMarksLoading } = useMyTeamMarksSummary(
    companyIdNumbers,
    employeeIdNumbers,
  );
  const employeeStatusList = useMyTeamTodayEmployeeStatusList(companyIdNumbers, employeeIdNumbers);
  const { handleViewTardinessDetail } = useMyTeamTardinessDetailNavigation();

  const handleViewAttendanceDetail = useCallback(() => {
    const today = formatDateToYYYYMMDD(new Date());
    void navigate(`${ROUTE_DEFINITIONS.LISTADO_MARCAJES.path}?dateFrom=${today}&dateTo=${today}`);
  }, [navigate]);

  const handleViewAbsenceDetail = useCallback(() => {
    const today = formatDateToYYYYMMDD(new Date());
    void navigate(`${ROUTE_DEFINITIONS.APROBAR_AUSENCIAS.path}?dateFrom=${today}&dateTo=${today}`);
  }, [navigate]);

  // Sin query params: la pantalla de Aprobación de HHEE administra sus propios
  // filtros de empresa/corte como estado interno, igual que las demás tarjetas de
  // este dashboard no pasan estado real al navegar (alcance confirmado con el usuario).
  const handleViewOvertimeDetail = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.APROBAR_HHEE.path);
  }, [navigate]);

  const handleViewMonthlyTardinessDetail = useCallback(() => {
    const query = monthlySummaryMonthlyCutId != null ? `?cutoffId=${monthlySummaryMonthlyCutId}` : '';
    void navigate(`${ROUTE_DEFINITIONS.APROBAR_ATRASOS.path}${query}`);
  }, [navigate, monthlySummaryMonthlyCutId]);

  // Unlike Horas extras/Atrasos, this card's "Detalle" navigates with the cutoff's date
  // range (not a cutoff id) — the target screen (Aprobar ausencias) filters by dateFrom/
  // dateTo, same as the "hoy" Ausentes card's handler above.
  const handleViewMonthlyAbsenceDetail = useCallback(() => {
    if (!monthlyAbsenceSummary) return;
    const dateFrom = formatDateToYYYYMMDD(new Date(monthlyAbsenceSummary.dateFrom));
    const dateTo = formatDateToYYYYMMDD(new Date(monthlyAbsenceSummary.dateTo));
    void navigate(`${ROUTE_DEFINITIONS.APROBAR_AUSENCIAS.path}?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }, [navigate, monthlyAbsenceSummary]);

  // Same target as the "hoy" Asistencia/Marcajes cards' handler (marks report), but with
  // the cutoff's date range instead of today — mirrors handleViewMonthlyAbsenceDetail.
  const handleViewMonthlyMarksDetail = useCallback(() => {
    if (!monthlyMarksSummary) return;
    const dateFrom = formatDateToYYYYMMDD(new Date(monthlyMarksSummary.dateFrom));
    const dateTo = formatDateToYYYYMMDD(new Date(monthlyMarksSummary.dateTo));
    void navigate(`${ROUTE_DEFINITIONS.LISTADO_MARCAJES.path}?dateFrom=${dateFrom}&dateTo=${dateTo}`);
  }, [navigate, monthlyMarksSummary]);

  const attendanceSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('attendance:dashboard.myTeam.attendanceSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewAttendanceDetail,
    },
  ], [translate, handleViewAttendanceDetail]);

  const tardinessSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewTardinessDetail',
      label: translate('attendance:dashboard.myTeam.tardinessSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: () => { void handleViewTardinessDetail(); },
    },
  ], [translate, handleViewTardinessDetail]);

  const absenceSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewAbsenceDetail',
      label: translate('attendance:dashboard.myTeam.absenceSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewAbsenceDetail,
    },
  ], [translate, handleViewAbsenceDetail]);

  // Reuses the same "Ver detalle" destination as the Asistencia card (marks report
  // filtered by today) — this card just needs its own menu items array so the
  // `id`s (and their test ids) don't collide with the other cards.
  const marksSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewMarksDetail',
      label: translate('attendance:dashboard.myTeam.marksSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewAttendanceDetail,
    },
  ], [translate, handleViewAttendanceDetail]);

  const overtimeSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewOvertimeDetail',
      label: translate('attendance:dashboard.myTeam.overtimeSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewOvertimeDetail,
    },
  ], [translate, handleViewOvertimeDetail]);

  const monthlyTardinessSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewMonthlyTardinessDetail',
      label: translate('attendance:dashboard.myTeam.monthlyTardinessSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewMonthlyTardinessDetail,
    },
  ], [translate, handleViewMonthlyTardinessDetail]);

  const monthlyAbsenceSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewMonthlyAbsenceDetail',
      label: translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewMonthlyAbsenceDetail,
    },
  ], [translate, handleViewMonthlyAbsenceDetail]);

  const monthlyMarksSummaryMenuItems: ActionMenuItem[] = useMemo(() => [
    {
      id: 'viewMonthlyMarksDetail',
      label: translate('attendance:dashboard.myTeam.monthlyMarksSummary.viewDetail'),
      icon: 'eye',
      color: colors.important.main,
      onClick: handleViewMonthlyMarksDetail,
    },
  ], [translate, handleViewMonthlyMarksDetail]);

  const allLabel = translate('attendance:dashboard.myTeam.filters.all');
  const allCompaniesLabel = translate('attendance:dashboard.myTeam.filters.allCompanies');
  const allEmployeesLabel = translate('attendance:dashboard.myTeam.filters.allEmployees');

  const formatSectionCollapsedLabel = useCallback((selected: SelectorOption[]) => {
    const rest = selected.length - 1;
    return rest > 0
      ? translate('attendance:dashboard.myTeam.filters.firstPlusRest', { label: selected[0]?.label ?? '', rest })
      : (selected[0]?.label ?? '');
  }, [translate]);

  const formatEmployeeCollapsedLabel = useCallback((selected: SelectorOption[]) => (
    translate('attendance:dashboard.myTeam.filters.selectedMasculine', { count: selected.length })
  ), [translate]);

  // Builds the bar chart points for one evolution series (ASIS-5413): abbreviated month
  // label from the point's `month` (1-12 → monthsShort index 0-11), and the last point
  // (most recent — the selected cutoff's month) highlighted.
  const buildChartPoints = useCallback((
    series: { year: number; month: number; value: number }[],
  ): BarChartDataPoint[] => series.map((point, index) => ({
    label: translate(`attendance:dashboard.monthsShort.${point.month - 1}`),
    value: point.value,
    isHighlighted: index === series.length - 1,
  })), [translate]);

  return (
    <div className="flex flex-col gap-6" data-testid="my-team-tab-content">
      <Card elevation={2} className="rounded-2xl" data-testid="my-team-filters-panel">
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-3">
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.section')}
            value={filters.sectionCodes}
            options={catalogOptions.sectionOptions}
            onChange={handlers.onSectionChange}
            multiple
            searchable
            formatCollapsedLabel={formatSectionCollapsedLabel}
            isLoading={isLoading}
            placeholder={allLabel}
            data-testid="my-team-section-selector"
          />
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.company')}
            value={filters.companyIds}
            options={catalogOptions.companyOptions}
            onChange={handlers.onCompanyChange}
            multiple
            searchable
            isLoading={isLoading}
            placeholder={allCompaniesLabel}
            data-testid="my-team-company-selector"
          />
          <Selector
            label={translate('attendance:dashboard.myTeam.filters.employee')}
            value={filters.employeeIds}
            options={catalogOptions.employeeOptions}
            onChange={handlers.onEmployeeChange}
            multiple
            searchable
            formatCollapsedLabel={formatEmployeeCollapsedLabel}
            isLoading={isLoading || isEmployeesLoading}
            placeholder={allEmployeesLabel}
            data-testid="my-team-employee-selector"
          />
        </div>
      </Card>

      <div className="flex flex-col gap-6">
        {sections
          .filter((section) => visibleSectionCodes.includes(section.code))
          .map((section) => {
            const sectionTestId = `my-team-section-${section.code}`;

            if (section.code === RESUMEN_MES_SECTION_CODE) {
              return (
                <Card
                  key={section.code}
                  elevation={1}
                  className="rounded-2xl"
                  padding="24px"
                  data-testid={sectionTestId}
                >
                  <div className="flex flex-col gap-4">
                    <Text
                      variant="body-medium"
                      fontFamily="montserrat"
                      color={colors.grays.neutral33}
                      style={{ fontWeight: 700 }}
                    >
                      {section.label}
                    </Text>

                    <MyTeamMonthlySummaryFilters
                      selectedCompanyId={monthlySummaryFilters.selectedCompanyId}
                      selectedYear={monthlySummaryFilters.selectedYear}
                      selectedMonth={monthlySummaryFilters.selectedMonth}
                      selectedMonthlyCutId={monthlySummaryFilters.selectedMonthlyCutId}
                      companyOptions={monthlySummaryFilters.companyOptions}
                      yearOptions={monthlySummaryFilters.yearOptions}
                      monthOptions={monthlySummaryFilters.monthOptions}
                      descriptionOptions={monthlySummaryFilters.descriptionOptions}
                      isLoadingCutoffs={monthlySummaryFilters.isLoadingCutoffs}
                      hasAvailableCutoffs={monthlySummaryFilters.hasAvailableCutoffs}
                      isFiltersDisabled={monthlySummaryFilters.isFiltersDisabled}
                      onCompanyChange={monthlySummaryFilters.onCompanyChange}
                      onYearChange={monthlySummaryFilters.onYearChange}
                      onMonthChange={monthlySummaryFilters.onMonthChange}
                      onDescriptionChange={monthlySummaryFilters.onDescriptionChange}
                      data-testid={`${sectionTestId}-filters`}
                    />
                    <div className="flex flex-row flex-wrap gap-6">
                      {(() => {
                        if (isOvertimeLoading || !overtimeSummary) {
                          return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-overtime`} />;
                        }

                        const overtimeColor = overtimeSummary.isEmpty
                          ? MY_TEAM_EMPTY_STATE_COLOR
                          : getMonthlyOvertimeColor(overtimeSummary.percentage);
                        const formatHours = (hours: number) => `${hours.toFixed(1).replace('.', ',')} hrs`;

                        return (
                          <DonutSummaryCard
                            title={translate('attendance:dashboard.myTeam.overtimeSummary.cardTitle')}
                            percentage={overtimeSummary.percentage}
                            color={overtimeColor}
                            percentageLabel={`${overtimeSummary.percentage}%`}
                            headerAction={(
                              <ActionMenu
                                items={overtimeSummaryMenuItems}
                                ariaLabel={translate('attendance:dashboard.myTeam.overtimeSummary.viewDetail')}
                              />
                            )}
                            rows={[
                              {
                                label: translate('attendance:dashboard.myTeam.overtimeSummary.approvedRowLabel'),
                                value: formatHours(overtimeSummary.approvedHours),
                                valueColor: overtimeColor,
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.overtimeSummary.pendingRowLabel'),
                                value: formatHours(overtimeSummary.pendingHours),
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.overtimeSummary.totalRowLabel'),
                                value: formatHours(overtimeSummary.totalHours),
                              },
                            ]}
                            data-testid={`${sectionTestId}-overtime`}
                          />
                        );
                      })()}

                      {(() => {
                        if (isTardinessMonthlyLoading || !tardinessMonthlySummary) {
                          return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-tardiness`} />;
                        }

                        const tardinessMonthlyColor = tardinessMonthlySummary.isEmpty
                          ? MY_TEAM_EMPTY_STATE_COLOR
                          : getMonthlyTardinessColor(tardinessMonthlySummary.percentage);
                        const formatHours = (hours: number) => `${hours.toFixed(1).replace('.', ',')} hrs`;

                        return (
                          <DonutSummaryCard
                            title={translate('attendance:dashboard.myTeam.monthlyTardinessSummary.cardTitle')}
                            percentage={tardinessMonthlySummary.percentage}
                            color={tardinessMonthlyColor}
                            percentageLabel={`${tardinessMonthlySummary.percentage}%`}
                            headerAction={(
                              <ActionMenu
                                items={monthlyTardinessSummaryMenuItems}
                                ariaLabel={translate('attendance:dashboard.myTeam.monthlyTardinessSummary.viewDetail')}
                              />
                            )}
                            rows={[
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyTardinessSummary.approvedRowLabel'),
                                value: formatHours(tardinessMonthlySummary.approvedHours),
                                valueColor: tardinessMonthlyColor,
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyTardinessSummary.pendingRowLabel'),
                                value: formatHours(tardinessMonthlySummary.pendingHours),
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyTardinessSummary.totalRowLabel'),
                                value: formatHours(tardinessMonthlySummary.totalHours),
                              },
                            ]}
                            data-testid={`${sectionTestId}-tardiness`}
                          />
                        );
                      })()}

                      {(() => {
                        if (isAbsenceMonthlyLoading || !monthlyAbsenceSummary) {
                          return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-absence-month`} />;
                        }

                        const monthlyAbsenceColor = monthlyAbsenceSummary.isEmpty
                          ? MY_TEAM_EMPTY_STATE_COLOR
                          : getMonthlyAbsenceColor(monthlyAbsenceSummary.percentage);
                        const formatDays = (days: number) =>
                          translate('attendance:dashboard.myTeam.daysUnit', { count: days });

                        return (
                          <DonutSummaryCard
                            title={translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.cardTitle')}
                            percentage={monthlyAbsenceSummary.percentage}
                            color={monthlyAbsenceColor}
                            percentageLabel={`${monthlyAbsenceSummary.percentage}%`}
                            headerAction={(
                              <ActionMenu
                                items={monthlyAbsenceSummaryMenuItems}
                                ariaLabel={translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.viewDetail')}
                              />
                            )}
                            rows={[
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.approvedRowLabel'),
                                value: formatDays(monthlyAbsenceSummary.approvedDays),
                                valueColor: monthlyAbsenceColor,
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.pendingRowLabel'),
                                value: formatDays(monthlyAbsenceSummary.pendingDays),
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.totalRowLabel'),
                                value: formatDays(monthlyAbsenceSummary.totalDays),
                              },
                            ]}
                            data-testid={`${sectionTestId}-absence-month`}
                          />
                        );
                      })()}

                      {(() => {
                        if (isMarksMonthlyLoading || !monthlyMarksSummary) {
                          return <DonutSummaryCardSkeleton rows={2} data-testid={`${sectionTestId}-marks-month`} />;
                        }

                        const inPersonColor = monthlyMarksSummary.isEmpty
                          ? MY_TEAM_EMPTY_STATE_COLOR
                          : colors.primary.main;
                        const remoteColor = monthlyMarksSummary.isEmpty
                          ? MY_TEAM_EMPTY_STATE_COLOR
                          : colors.dashboardStatus.remoto;

                        return (
                          <DonutSummaryCard
                            title={translate('attendance:dashboard.myTeam.monthlyMarksSummary.cardTitle')}
                            segments={[
                              {
                                percentage: monthlyMarksSummary.percentageInPerson,
                                color: inPersonColor,
                              },
                              {
                                percentage: monthlyMarksSummary.percentageRemote,
                                color: remoteColor,
                              },
                            ]}
                            labelColor={inPersonColor}
                            percentageLabel={`${monthlyMarksSummary.percentageInPerson}%`}
                            percentageSubLabel={translate('attendance:dashboard.myTeam.monthlyMarksSummary.presentialRowLabel')}
                            headerAction={(
                              <ActionMenu
                                items={monthlyMarksSummaryMenuItems}
                                ariaLabel={translate('attendance:dashboard.myTeam.monthlyMarksSummary.viewDetail')}
                              />
                            )}
                            rows={[
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyMarksSummary.presentialRowLabel'),
                                value: `${monthlyMarksSummary.percentageInPerson}%`,
                                valueColor: inPersonColor,
                              },
                              {
                                label: translate('attendance:dashboard.myTeam.monthlyMarksSummary.remoteRowLabel'),
                                value: `${monthlyMarksSummary.percentageRemote}%`,
                                valueColor: remoteColor,
                              },
                            ]}
                            data-testid={`${sectionTestId}-marks-month`}
                          />
                        );
                      })()}
                    </div>

                    {!isEvolutionLoading && monthlyEvolution && (
                      <div className="flex flex-row flex-wrap gap-6">
                        <BarChartCard
                          title={translate('attendance:dashboard.myTeam.monthlyEvolution.overtimeChartTitle')}
                          data={buildChartPoints(monthlyEvolution.overtimeSeries)}
                          formatTooltipValue={(value) => `${value.toFixed(1).replace('.', ',')} hrs`}
                          headerAction={(
                            <ActionMenu
                              items={overtimeSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.overtimeSummary.viewDetail')}
                            />
                          )}
                          data-testid={`${sectionTestId}-overtime-evolution`}
                        />
                        <BarChartCard
                          title={translate('attendance:dashboard.myTeam.monthlyEvolution.tardinessChartTitle')}
                          data={buildChartPoints(monthlyEvolution.tardinessSeries)}
                          formatTooltipValue={(value) => `${value.toFixed(1).replace('.', ',')} hrs`}
                          headerAction={(
                            <ActionMenu
                              items={monthlyTardinessSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.monthlyTardinessSummary.viewDetail')}
                            />
                          )}
                          data-testid={`${sectionTestId}-tardiness-evolution`}
                        />
                        <BarChartCard
                          title={translate('attendance:dashboard.myTeam.monthlyEvolution.absenceChartTitle')}
                          data={buildChartPoints(monthlyEvolution.absenceSeries)}
                          formatTooltipValue={(value) => translate('attendance:dashboard.myTeam.daysUnit', { count: value })}
                          headerAction={(
                            <ActionMenu
                              items={monthlyAbsenceSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.monthlyAbsenceSummary.viewDetail')}
                            />
                          )}
                          data-testid={`${sectionTestId}-absence-evolution`}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              );
            }

            if (section.code !== SITUACION_ASISTENCIA_SECTION_CODE) {
              return (
                <Card key={section.code} elevation={1} className="rounded-2xl" data-testid={sectionTestId}>
                  <PlaceholderContent label={section.label} />
                </Card>
              );
            }

            return (
              <Card
                key={section.code}
                elevation={1}
                className="rounded-2xl"
                padding="24px"
                data-testid={sectionTestId}
              >
                <div className="flex flex-col gap-4">
                  <Text
                    variant="body-medium"
                    fontFamily="montserrat"
                    color={colors.grays.neutral33}
                    style={{ fontWeight: 700 }}
                  >
                    {section.label}{' '}
                    <span style={{ fontWeight: 400 }}>{buildSectionHeaderDate()}</span>
                  </Text>

                  <div className="flex flex-row flex-wrap gap-6">
                    {(() => {
                      if (isSummaryLoading || !summary) {
                        return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-attendance`} />;
                      }

                      const summaryColor = summary.isEmpty
                        ? MY_TEAM_EMPTY_STATE_COLOR
                        : getAttendanceSituationColor(summary.percentage);

                      return (
                        <DonutSummaryCard
                          title={translate('attendance:dashboard.myTeam.attendanceSummary.cardTitle')}
                          percentage={summary.percentage}
                          color={summaryColor}
                          percentageLabel={`${summary.percentage}%`}
                          headerAction={(
                            <ActionMenu
                              items={attendanceSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.attendanceSummary.viewDetail')}
                            />
                          )}
                          rows={[
                            {
                              label: translate('attendance:dashboard.myTeam.attendanceSummary.presentRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: summary.presentCount }),
                              valueColor: summaryColor,
                            },
                            {
                              label: translate('attendance:dashboard.myTeam.attendanceSummary.totalRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: summary.totalSupervisedCount }),
                            },
                          ]}
                          data-testid={`${sectionTestId}-attendance`}
                        />
                      );
                    })()}

                    {(() => {
                      if (isTardinessLoading || !tardinessSummary) {
                        return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-tardiness`} />;
                      }

                      const tardinessColor = tardinessSummary.isEmpty
                        ? MY_TEAM_EMPTY_STATE_COLOR
                        : getTardinessSituationColor(tardinessSummary.percentage);

                      return (
                        <DonutSummaryCard
                          title={translate('attendance:dashboard.myTeam.tardinessSummary.cardTitle')}
                          percentage={tardinessSummary.percentage}
                          color={tardinessColor}
                          percentageLabel={`${tardinessSummary.percentage}%`}
                          headerAction={(
                            <ActionMenu
                              items={tardinessSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.tardinessSummary.viewDetail')}
                            />
                          )}
                          rows={[
                            {
                              label: translate('attendance:dashboard.myTeam.tardinessSummary.lateRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: tardinessSummary.lateCount }),
                              valueColor: tardinessColor,
                            },
                            {
                              label: translate('attendance:dashboard.myTeam.tardinessSummary.totalRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: tardinessSummary.totalSupervisedCount }),
                            },
                          ]}
                          data-testid={`${sectionTestId}-tardiness`}
                        />
                      );
                    })()}

                    {(() => {
                      if (isAbsenceLoading || !absenceSummary) {
                        return <DonutSummaryCardSkeleton data-testid={`${sectionTestId}-absence`} />;
                      }

                      const absenceColor = absenceSummary.isEmpty
                        ? MY_TEAM_EMPTY_STATE_COLOR
                        : getAbsenceSituationColor(absenceSummary.percentage);

                      return (
                        <DonutSummaryCard
                          title={translate('attendance:dashboard.myTeam.absenceSummary.cardTitle')}
                          percentage={absenceSummary.percentage}
                          color={absenceColor}
                          percentageLabel={`${absenceSummary.percentage}%`}
                          headerAction={(
                            <ActionMenu
                              items={absenceSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.absenceSummary.viewDetail')}
                            />
                          )}
                          rows={[
                            {
                              label: translate('attendance:dashboard.myTeam.absenceSummary.unjustifiedRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: absenceSummary.unjustifiedCount }),
                              valueColor: absenceColor,
                            },
                            {
                              label: translate('attendance:dashboard.myTeam.absenceSummary.justifiedRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: absenceSummary.justifiedCount }),
                            },
                            {
                              label: translate('attendance:dashboard.myTeam.absenceSummary.totalRowLabel'),
                              value: translate('attendance:dashboard.myTeam.employeesUnit', { count: absenceSummary.totalSupervisedCount }),
                            },
                          ]}
                          data-testid={`${sectionTestId}-absence`}
                        />
                      );
                    })()}

                    {(() => {
                      if (isMarksLoading || !marksSummary) {
                        return <DonutSummaryCardSkeleton rows={2} data-testid={`${sectionTestId}-marks`} />;
                      }

                      const inPersonColor = marksSummary.isEmpty
                        ? MY_TEAM_EMPTY_STATE_COLOR
                        : colors.primary.main;
                      const remoteColor = marksSummary.isEmpty
                        ? MY_TEAM_EMPTY_STATE_COLOR
                        : colors.dashboardStatus.remoto;

                      return (
                        <DonutSummaryCard
                          title={translate('attendance:dashboard.myTeam.marksSummary.cardTitle')}
                          segments={[
                            { percentage: marksSummary.percentageInPerson, color: inPersonColor },
                            { percentage: marksSummary.percentageRemote, color: remoteColor },
                          ]}
                          labelColor={inPersonColor}
                          percentageLabel={`${marksSummary.percentageInPerson}%`}
                          percentageSubLabel={translate('attendance:dashboard.myTeam.marksSummary.presentialRowLabel')}
                          headerAction={(
                            <ActionMenu
                              items={marksSummaryMenuItems}
                              ariaLabel={translate('attendance:dashboard.myTeam.marksSummary.viewDetail')}
                            />
                          )}
                          rows={[
                            {
                              label: translate('attendance:dashboard.myTeam.marksSummary.presentialRowLabel'),
                              value: `${marksSummary.percentageInPerson}%`,
                              valueColor: inPersonColor,
                            },
                            {
                              label: translate('attendance:dashboard.myTeam.marksSummary.remoteRowLabel'),
                              value: `${marksSummary.percentageRemote}%`,
                              valueColor: remoteColor,
                            },
                          ]}
                          data-testid={`${sectionTestId}-marks`}
                        />
                      );
                    })()}
                  </div>

                  <div
                    className="flex flex-col gap-3"
                    data-testid={`${sectionTestId}-employee-status-list`}
                  >
                    <div className="flex flex-row flex-wrap items-end gap-4">
                      <div className="w-full sm:w-64">
                        <Selector
                          label={translate('attendance:dashboard.myTeam.employeeStatusList.filters.status')}
                          value={employeeStatusList.statusFilter}
                          options={employeeStatusList.statusOptions}
                          onChange={employeeStatusList.onStatusFilterChange}
                          multiple
                          placeholder={allLabel}
                          data-testid={`${sectionTestId}-status-selector`}
                        />
                      </div>
                    </div>

                    <Table
                      columns={employeeStatusList.columns}
                      data={employeeStatusList.rows}
                      totalRows={employeeStatusList.total}
                      isLoading={employeeStatusList.loading}
                      searchPlaceholder={translate('attendance:dashboard.myTeam.employeeStatusList.searchPlaceholder')}
                      onSearch={employeeStatusList.onSearch}
                      serverSideSort
                      initialSort={{ key: 'statusCode', direction: 'asc' }}
                      onSortChange={employeeStatusList.onSortChange}
                      getRowTextColor={employeeStatusList.getRowTextColor}
                      pagination={{
                        page: employeeStatusList.page,
                        pageSize: employeeStatusList.pageSize,
                        total: employeeStatusList.total,
                        onChange: employeeStatusList.setPage,
                        onPageSizeChange: employeeStatusList.setPageSize,
                      }}
                      data-testid={`${sectionTestId}-employee-status-table`}
                    />
                  </div>
                </div>
              </Card>
            );
          })}
      </div>
    </div>
  );
};

export default MyTeamTabContent;
